
import { Component, Vue, Prop } from "vue-property-decorator"

import Guid from "@/scripts/guid"

// @ts-ignore
import VueGridLayout from "@sureview/vue-grid"
import vSelect from "vue-select"

import Graph from "@/components/Dashboard/Graph.vue"

import { ReportSection } from "@/types/report"
import { Insight } from "@/types/insights"

interface DisplayCell {
	i: string | null
	x?: number
	y?: number
	h?: number
	w?: number
	newCell?: boolean
	contents: any
	isNew?: boolean
}

@Component({
	components: {
		"grid-layout": VueGridLayout.GridLayout,
		"grid-item": VueGridLayout.GridItem,
		"graph": Graph,
		"v-select": vSelect
	}
})
export default class ReportSectionLayout extends Vue {
	// --- Props ---

	@Prop()
	value!: ReportSection

	@Prop()
	insights!: Insight[]

	// --- Data ---

	addCell: DisplayCell | null = null
	confirmDelete = false

	layout: DisplayCell[] = []

	// --- Computed Properties ---

	/**
	 * @summary Gets row height in pixels
	 */
	public get scaledRowHeight (): number {
		return 280 // Height in pixels
	}

	private get rowCount () {
		if (!this.layout) {
			return 0
		}
		return (this.layout.maxMap(cell => cell.y! + cell.h!) || 0) + 1
	}

	private get sectionlayout (): DisplayCell[] {
		if (!this.value) return []
		return this.value.contents.map(cell => ({
			i: cell.id!,
			h: cell.cellSize.height ?? 1,
			w: cell.cellSize.width ?? 1,
			x: cell.cellPosition.x,
			y: cell.cellPosition.y,
			contents: cell.contents
		}))
	}

	// --- Lifecycle Hooks ---

	mounted () {
		if (this.value) {
			this.layout = this.sectionlayout
		}
	}

	// --- Event Handlers ---

	/**
	 * @summary Allows a user to click to add a new insight cell
	 */
	addInsight () {
		if (this.addCell) return

		this.addCell = {
			i: null,
			newCell: true,
			h: 1,
			w: 1,
			contents: {}
		}
	}

	/**
	 * @summary Ends adding mode. Sets ID of added cell
	 */
	finishAdd () {
		this.layout.forEach((layoutCell: any) => {
			if (!layoutCell.i) {
				layoutCell.i = Guid.newGuid()
			}
		})
		this.addCell = null
		this.updateLayout(this.layout)
	}

	/**
	 * @summary Switches layout to given columns
	 * @param {number} columns = number of columns (1 or 2)
	 */
	setColumns (columns: number) {
		this.$emit("input", {
			...this.value,
			columns
		})
	}

	/**
	 * @summary Remove cell from layout
	 * @param {DisplayCell} cell - cell to remove
	 */
	deleteCell (cell: DisplayCell) {
		if (cell.i) {
			this.layout = this.layout.filter(c => c.i !== cell.i)
			this.updateLayout(this.layout)
		}
	}

	/**
	 * @summary Emits event to remove this section from the report
	 */
	deleteCurrentSection () {
		this.$emit("remove")
	}

	// --- Utils ---

	/**
	 * @summary Raise updated layout
	 * @param {DisplayCell[]} newLayout - Changed layout
	 */
	updateLayout (newLayout: DisplayCell[]) {
		const contents = newLayout.filter(c => !c.isNew).map(c => ({
			id: c.i ?? Guid.newGuid(),
			cellSize: {
				height: c.h ?? 1,
				width: c.w ?? 1
			},
			cellPosition: {
				x: c.x ?? 0,
				y: c.y ?? 0
			},
			contents: c.contents ?? {},
			cellType: 0
		}))

		this.$emit("input", {
			...this.value,
			contents
		})
	}
}

